import React, { useState, useEffect, useRef } from "react";
import { supabase } from "./supabaseClient";

const WEIGHT_CLASSES = [
  "Strawweight (up to 105 lbs)",
  "Flyweight (up to 112 lbs)",
  "Bantamweight (up to 118 lbs)",
  "Featherweight (up to 126 lbs)",
  "Lightweight (up to 135 lbs)",
  "Welterweight (up to 147 lbs)",
  "Middleweight (up to 160 lbs)",
  "Light Heavyweight (up to 175 lbs)",
  "Cruiserweight (up to 200 lbs)",
  "Heavyweight (over 200 lbs)"
];

const EXPERIENCE_LEVELS = [
  "Beginner (0-1 years)",
  "Amateur (1-3 years)",
  "Intermediate (3-5 years)",
  "Advanced (5-10 years)",
  "Professional (10+ years)"
];

const BOXING_STYLES = [
  { value: "orthodox", label: "Orthodox" },
  { value: "southpaw", label: "Southpaw" },
  { value: "philly_shell", label: "Philly Shell" },
  { value: "peek_a_boo", label: "Peek-a-Boo" },
  { value: "soviet", label: "Soviet/Kronk" },
  { value: "swarmer", label: "Swarmer" }
];

const inputStyle = {
  width:"100%",padding:"14px",
  backgroundColor:"rgba(255,255,255,0.04)",
  color:"white",border:"1px solid rgba(255,255,255,0.08)",
  borderRadius:"12px",fontSize:"0.95rem",
  fontFamily:"Inter, sans-serif",boxSizing:"border-box",
  outline:"none",marginBottom:"12px"
};

function Profile({ user, onAvatarUpdate }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [editing, setEditing] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);

  const [fullName, setFullName] = useState("");
  const [age, setAge] = useState("");
  const [weightClass, setWeightClass] = useState("");
  const [experience, setExperience] = useState("");
  const [boxingStyle, setBoxingStyle] = useState("orthodox");
  const [gymName, setGymName] = useState("");
  const [coachingStyle, setCoachingStyle] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");

  useEffect(() => {
    supabase.from("profiles").select("*").eq("id", user.id).single().then(({ data }) => {
      if (data) {
        setProfile(data);
        setFullName(data.full_name || "");
        setAge(data.age || "");
        setWeightClass(data.weight_class || "");
        setExperience(data.experience || "");
        setBoxingStyle(data.boxing_style || "orthodox");
        setGymName(data.gym_name || "");
        setCoachingStyle(data.coaching_style || "");
        setAvatarUrl(data.avatar_url || "");
      }
      setLoading(false);
    });
  }, [user]);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingAvatar(true);
    const fileExt = file.name.split(".").pop();
    const fileName = user.id + "." + fileExt;
    const { error: uploadError } = await supabase.storage.from("avatars").upload(fileName, file, { upsert: true });
    if (!uploadError) {
      const { data } = supabase.storage.from("avatars").getPublicUrl(fileName);
      const url = data.publicUrl + "?t=" + Date.now();
      await supabase.from("profiles").update({ avatar_url: url }).eq("id", user.id);
      setAvatarUrl(url);
      if (onAvatarUpdate) onAvatarUpdate(url);
    }
    setUploadingAvatar(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const updates = profile?.role === "fighter"
      ? { full_name: fullName, age: parseInt(age) || null, weight_class: weightClass, experience, boxing_style: boxingStyle }
      : { full_name: fullName, gym_name: gymName, coaching_style: coachingStyle };
    await supabase.from("profiles").update(updates).eq("id", user.id);
    setSaving(false);
    setSaved(true);
    setEditing(false);
    setTimeout(() => setSaved(false), 3000);
  };

  const role = profile?.role || user.user_metadata?.role;

  if (loading) return (
    <div style={{textAlign:"center",padding:"60px",fontFamily:"Inter, sans-serif"}}>
      <p style={{color:"#555"}}>Loading profile...</p>
    </div>
  );

  return (
    <div style={{fontFamily:"Inter, sans-serif"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"30px"}}>
        <div style={{display:"flex",alignItems:"center",gap:"20px"}}>
          <div style={{position:"relative"}}>
            <div
              onClick={() => fileInputRef.current.click()}
              style={{width:"80px",height:"80px",borderRadius:"50%",background: avatarUrl ? "transparent" : "linear-gradient(135deg, #e94560, #ff6b35)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"2rem",cursor:"pointer",overflow:"hidden",border:"3px solid rgba(233,69,96,0.3)",position:"relative"}}>
              {avatarUrl ? (
                <img src={avatarUrl} alt="avatar" style={{width:"100%",height:"100%",objectFit:"cover"}} />
              ) : (
                role === "coach" ? "🎯" : "🥋"
              )}
              <div style={{position:"absolute",inset:0,backgroundColor:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",opacity:0,transition:"opacity 0.2s"}}
                onMouseEnter={e => e.currentTarget.style.opacity=1}
                onMouseLeave={e => e.currentTarget.style.opacity=0}>
                <span style={{color:"white",fontSize:"0.7rem",fontWeight:"600",textAlign:"center"}}>Change Photo</span>
              </div>
            </div>
            {uploadingAvatar && (
              <div style={{position:"absolute",inset:0,borderRadius:"50%",backgroundColor:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <span style={{color:"white",fontSize:"0.7rem"}}>...</span>
              </div>
            )}
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarUpload} style={{display:"none"}} />
          </div>
          <div>
            <h2 style={{margin:0,fontSize:"1.5rem",fontWeight:"700"}}>{fullName || user.email}</h2>
            <p style={{margin:"4px 0 0 0",color:"#555",fontSize:"0.85rem",textTransform:"uppercase",letterSpacing:"1px"}}>{role === "coach" ? "Coach" : "Fighter"}</p>
            <p style={{margin:"4px 0 0 0",color:"#333",fontSize:"0.8rem"}}>Click photo to change</p>
          </div>
        </div>
        <button onClick={() => setEditing(!editing)} style={{padding:"10px 20px",background: editing ? "rgba(255,255,255,0.05)" : "rgba(233,69,96,0.15)",color: editing ? "#666" : "#e94560",border: editing ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(233,69,96,0.3)",borderRadius:"10px",cursor:"pointer",fontFamily:"Inter, sans-serif",fontSize:"0.9rem",fontWeight:"600"}}>
          {editing ? "Cancel" : "Edit Profile"}
        </button>
      </div>

      <div style={{background:"rgba(255,255,255,0.02)",borderRadius:"16px",padding:"25px",border:"1px solid rgba(255,255,255,0.06)",marginBottom:"20px"}}>
        <p style={{color:"#555",fontSize:"0.75rem",textTransform:"uppercase",letterSpacing:"1px",marginBottom:"15px"}}>Account Info</p>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"15px"}}>
          <div><p style={{color:"#444",margin:"0 0 4px 0",fontSize:"0.8rem"}}>Email</p><p style={{color:"white",margin:0,fontSize:"0.95rem"}}>{user.email}</p></div>
          <div><p style={{color:"#444",margin:"0 0 4px 0",fontSize:"0.8rem"}}>Member Since</p><p style={{color:"white",margin:0,fontSize:"0.95rem"}}>{new Date(user.created_at).toLocaleDateString("en-US",{month:"long",year:"numeric"})}</p></div>
        </div>
      </div>

      {!editing ? (
        <div style={{background:"rgba(255,255,255,0.02)",borderRadius:"16px",padding:"25px",border:"1px solid rgba(255,255,255,0.06)"}}>
          <p style={{color:"#555",fontSize:"0.75rem",textTransform:"uppercase",letterSpacing:"1px",marginBottom:"15px"}}>Profile Details</p>
          {role === "fighter" ? (
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"15px"}}>
              <div><p style={{color:"#444",margin:"0 0 4px 0",fontSize:"0.8rem"}}>Full Name</p><p style={{color:"white",margin:0}}>{fullName || "Not set"}</p></div>
              <div><p style={{color:"#444",margin:"0 0 4px 0",fontSize:"0.8rem"}}>Age</p><p style={{color:"white",margin:0}}>{age || "Not set"}</p></div>
              <div><p style={{color:"#444",margin:"0 0 4px 0",fontSize:"0.8rem"}}>Weight Class</p><p style={{color:"white",margin:0,fontSize:"0.85rem"}}>{weightClass || "Not set"}</p></div>
              <div><p style={{color:"#444",margin:"0 0 4px 0",fontSize:"0.8rem"}}>Experience</p><p style={{color:"white",margin:0,fontSize:"0.85rem"}}>{experience || "Not set"}</p></div>
              <div><p style={{color:"#444",margin:"0 0 4px 0",fontSize:"0.8rem"}}>Primary Style</p><p style={{color:"#e94560",margin:0,fontWeight:"600",textTransform:"capitalize"}}>{boxingStyle || "Not set"}</p></div>
            </div>
          ) : (
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"15px"}}>
              <div><p style={{color:"#444",margin:"0 0 4px 0",fontSize:"0.8rem"}}>Full Name</p><p style={{color:"white",margin:0}}>{fullName || "Not set"}</p></div>
              <div><p style={{color:"#444",margin:"0 0 4px 0",fontSize:"0.8rem"}}>Gym Name</p><p style={{color:"white",margin:0}}>{gymName || "Not set"}</p></div>
              <div style={{gridColumn:"1/-1"}}><p style={{color:"#444",margin:"0 0 4px 0",fontSize:"0.8rem"}}>Coaching Philosophy</p><p style={{color:"white",margin:0}}>{coachingStyle || "Not set"}</p></div>
            </div>
          )}
        </div>
      ) : (
        <div style={{background:"rgba(255,255,255,0.02)",borderRadius:"16px",padding:"25px",border:"1px solid rgba(255,255,255,0.06)"}}>
          <p style={{color:"#555",fontSize:"0.75rem",textTransform:"uppercase",letterSpacing:"1px",marginBottom:"15px"}}>Edit Profile</p>
          <input type="text" placeholder="Full name" value={fullName} onChange={e => setFullName(e.target.value)} style={inputStyle} />
          {role === "fighter" && (
            <>
              <input type="number" placeholder="Age" value={age} onChange={e => setAge(e.target.value)} style={inputStyle} />
              <select value={weightClass} onChange={e => setWeightClass(e.target.value)} style={{...inputStyle,cursor:"pointer"}}>
                <option value="">Select weight class</option>
                {WEIGHT_CLASSES.map(w => <option key={w} value={w}>{w}</option>)}
              </select>
              <select value={experience} onChange={e => setExperience(e.target.value)} style={{...inputStyle,cursor:"pointer"}}>
                <option value="">Select experience level</option>
                {EXPERIENCE_LEVELS.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
              <label style={{display:"block",marginBottom:"10px",color:"#888",fontSize:"0.8rem",textTransform:"uppercase",letterSpacing:"1px"}}>Primary Style</label>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"8px",marginBottom:"16px"}}>
                {BOXING_STYLES.map(s => (
                  <div key={s.value} onClick={() => setBoxingStyle(s.value)} style={{padding:"10px",borderRadius:"10px",cursor:"pointer",textAlign:"center",border: boxingStyle===s.value ? "1px solid rgba(233,69,96,0.6)" : "1px solid rgba(255,255,255,0.06)",background: boxingStyle===s.value ? "rgba(233,69,96,0.15)" : "rgba(255,255,255,0.02)",color: boxingStyle===s.value ? "#e94560" : "#888",fontSize:"0.8rem",fontWeight:"600"}}>
                    {s.label}
                  </div>
                ))}
              </div>
            </>
          )}
          {role === "coach" && (
            <>
              <input type="text" placeholder="Gym name" value={gymName} onChange={e => setGymName(e.target.value)} style={inputStyle} />
              <input type="text" placeholder="Coaching philosophy" value={coachingStyle} onChange={e => setCoachingStyle(e.target.value)} style={inputStyle} />
            </>
          )}
          <button onClick={handleSave} disabled={saving} style={{width:"100%",padding:"15px",background: saving ? "rgba(255,255,255,0.05)" : "linear-gradient(135deg, #e94560, #ff6b35)",color: saving ? "#444" : "white",border:"none",borderRadius:"12px",fontSize:"1rem",fontWeight:"700",fontFamily:"Inter, sans-serif",cursor: saving ? "not-allowed" : "pointer",letterSpacing:"1px",textTransform:"uppercase"}}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
          {saved && <p style={{color:"#4caf50",textAlign:"center",marginTop:"12px",fontSize:"0.9rem"}}>✅ Profile saved!</p>}
        </div>
      )}
    </div>
  );
}

export default Profile;
